import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { IDEA_STATUSES, IDEA_PRIORITIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useBulkSelect, BulkBar } from "@/components/BulkBar";
import { Plus, Pencil, Trash2, Loader2, Lightbulb, GripVertical, X } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  high: "bg-[#F4E7E7] text-[#9D4848] border-[#E4CACA]",
  medium: "bg-[#F6EDD9] text-[#A87F31] border-[#EBD9AE]",
  low: "bg-[#F1F3F6] text-[#687585] border-[#DDE1E8]",
};
const COLUMN_ACCENT = {
  idea: "border-t-[#8A94A6]",
  next_up: "border-t-[#1B4383]",
  in_progress: "border-t-[#A87F31]",
  done: "border-t-[#527D63]",
};
const empty = { title: "", description: "", status: "idea", priority: "medium", subideas: [] };
const genSubId = () => `s${Math.random().toString(36).slice(2, 8)}`;

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const sel = useBulkSelect();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [subAdd, setSubAdd] = useState({}); // ideaId -> draft text

  const load = useCallback(() => {
    setLoading(true);
    api.get("/ideas").then((r) => setIdeas(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = (status = "idea") => setDialog({ ...empty, subideas: [], status });
  const openEdit = (idea) => setDialog({ id: idea.id, title: idea.title, description: idea.description, status: idea.status, priority: idea.priority, subideas: (idea.subideas || []).map((s) => ({ ...s })) });

  const save = async () => {
    if (!dialog.title.trim()) { toast.error("Give your idea a title."); return; }
    setSaving(true);
    const body = {
      title: dialog.title.trim(),
      description: dialog.description || "",
      status: dialog.status,
      priority: dialog.priority,
      subideas: (dialog.subideas || []).filter((s) => (s.text || "").trim()).map((s) => ({ id: s.id, text: s.text.trim(), done: !!s.done })),
    };
    try {
      if (dialog.id) await api.put(`/ideas/${dialog.id}`, body);
      else await api.post("/ideas", body);
      toast.success(dialog.id ? "Idea updated" : "Idea added");
      setDialog(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const patchIdea = async (idea, changes) => {
    const next = { ...idea, ...changes };
    setIdeas((prev) => prev.map((i) => (i.id === idea.id ? next : i)));
    try {
      await api.put(`/ideas/${idea.id}`, { title: next.title, description: next.description, status: next.status, priority: next.priority, subideas: next.subideas || [] });
    } catch (e) { toast.error(apiError(e)); load(); }
  };
  const addSub = (idea) => {
    const text = (subAdd[idea.id] || "").trim();
    if (!text) return;
    patchIdea(idea, { subideas: [...(idea.subideas || []), { id: genSubId(), text, done: false }] });
    setSubAdd((m) => ({ ...m, [idea.id]: "" }));
  };
  const toggleSub = (idea, sid) => patchIdea(idea, { subideas: (idea.subideas || []).map((s) => (s.id === sid ? { ...s, done: !s.done } : s)) });
  const removeSub = (idea, sid) => patchIdea(idea, { subideas: (idea.subideas || []).filter((s) => s.id !== sid) });

  const move = async (idea, status) => {
    setIdeas((prev) => prev.map((i) => (i.id === idea.id ? { ...i, status } : i)));
    try { await api.put(`/ideas/${idea.id}`, { title: idea.title, description: idea.description, status, priority: idea.priority, subideas: idea.subideas || [] }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (idea) => {
    if (!window.confirm(`Delete "${idea.title}"?`)) return;
    try { await api.delete(`/ideas/${idea.id}`); setIdeas((p) => p.filter((i) => i.id !== idea.id)); toast.success("Idea deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const bulkDelete = async () => {
    const ids = [...sel.selected];
    if (!window.confirm(`Delete ${ids.length} idea${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/ideas/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} idea${data.deleted !== 1 ? "s" : ""}`);
      sel.clear(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const byStatus = (s) => ideas.filter((i) => i.status === s);

  return (
    <AppLayout title="Ideas" actions={
      <Button onClick={() => openNew()} data-testid="new-idea-button"><Plus className="mr-2 h-4 w-4" />New Idea</Button>
    }>
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-white p-4">
        <div className="rounded-md bg-[#F6EDD9] p-2"><Lightbulb className="h-5 w-5 text-[#A87F31]" /></div>
        <div>
          <h2 className="font-heading text-base font-bold tracking-tight">Your Private Idea Backlog</h2>
          <p className="text-sm text-muted-foreground">Capture product ideas and move them from <span className="font-medium">Idea → Next Up → In Progress → Done</span> as you execute. Only you can see this.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {IDEA_STATUSES.map((col) => {
            const list = byStatus(col.value);
            return (
              <div key={col.value}
                onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== col.value) setDragOverCol(col.value); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={(e) => { e.preventDefault(); const idea = ideas.find((i) => i.id === draggingId); if (idea && idea.status !== col.value) move(idea, col.value); setDragOverCol(null); setDraggingId(null); }}
                className={`rounded-lg border border-t-4 border-border bg-slate-50/60 transition-shadow ${COLUMN_ACCENT[col.value]} ${dragOverCol === col.value ? "ring-2 ring-[#1B4383] ring-offset-1" : ""}`} data-testid={`idea-col-${col.value}`}>
                <div className="flex items-center justify-between px-4 py-3">
                  <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">{col.label}</h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 border border-border" data-testid={`idea-count-${col.value}`}>{list.length}</span>
                </div>
                <div className="space-y-3 px-3 pb-3">
                  {list.map((idea) => (
                    <div key={idea.id} draggable
                      onDragStart={(e) => { setDraggingId(idea.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                      className={`group cursor-grab rounded-md border border-border bg-white p-3 shadow-sm active:cursor-grabbing ${draggingId === idea.id ? "opacity-50" : ""}`} data-testid={`idea-card-${idea.id}`}>
                      <div className="mb-1.5 flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={sel.isSelected(idea.id)} onCheckedChange={() => sel.toggle(idea.id)} aria-label="Select idea" data-testid={`bulk-select-idea-${idea.id}`} />
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize tracking-wide ${PRIORITY_STYLE[idea.priority]}`}>{idea.priority}</span>
                        </div>
                        <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button onClick={() => openEdit(idea)} className="text-slate-400 hover:text-primary" data-testid={`idea-edit-${idea.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => remove(idea)} className="text-slate-400 hover:text-destructive" data-testid={`idea-delete-${idea.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                      <div className="font-medium text-slate-900">{idea.title}</div>
                      {idea.description && <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{idea.description}</p>}
                      {(idea.subideas || []).length > 0 && (
                        <ul className="mt-2 space-y-1" data-testid={`idea-subs-${idea.id}`}>
                          {idea.subideas.map((s) => (
                            <li key={s.id} className="group/sub flex items-start gap-1.5 text-xs" data-testid={`idea-sub-${idea.id}-${s.id}`}>
                              <Checkbox checked={!!s.done} onCheckedChange={() => toggleSub(idea, s.id)} className="mt-0.5 h-3.5 w-3.5" aria-label="Toggle sub-idea" data-testid={`idea-sub-toggle-${idea.id}-${s.id}`} />
                              <span className={`flex-1 ${s.done ? "text-slate-400 line-through" : "text-slate-600"}`}>{s.text}</span>
                              <button onClick={() => removeSub(idea, s.id)} className="text-slate-300 opacity-0 transition-opacity hover:text-destructive group-hover/sub:opacity-100" data-testid={`idea-sub-del-${idea.id}-${s.id}`}><X className="h-3 w-3" /></button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-2 flex items-center gap-1.5">
                        <Input value={subAdd[idea.id] || ""} onChange={(e) => setSubAdd((m) => ({ ...m, [idea.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(idea); } }}
                          onClick={(e) => e.stopPropagation()} placeholder="Add a sub-idea…" className="h-7 flex-1 text-xs" data-testid={`idea-sub-input-${idea.id}`} />
                        <button onClick={() => addSub(idea)} className="rounded-md border border-border p-1.5 text-slate-400 hover:border-primary hover:text-primary" title="Add sub-idea" data-testid={`idea-sub-add-${idea.id}`}><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="mt-2.5 flex items-center gap-1.5 border-t border-border pt-2">
                        <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                        <Select value={idea.status} onValueChange={(v) => move(idea, v)}>
                          <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`idea-move-${idea.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{IDEA_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {!list.length && (
                    <button onClick={() => openNew(col.value)} className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 py-4 text-xs text-slate-400 hover:border-primary hover:text-primary" data-testid={`idea-add-${col.value}`}>
                      <Plus className="h-3.5 w-3.5" /> Add idea
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BulkBar count={sel.selected.size} onClear={sel.clear} onDelete={bulkDelete} canDelete deleting={bulkBusy} />

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="idea-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? "Edit Idea" : "New Idea"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={dialog.title} onChange={(e) => setDialog({ ...dialog, title: e.target.value })} placeholder="e.g. WhatsApp alerts for new supplier quotes" data-testid="idea-title" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea rows={4} value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} placeholder="What is it, why it matters, how it could work..." data-testid="idea-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={dialog.status} onValueChange={(v) => setDialog({ ...dialog, status: v })}>
                    <SelectTrigger data-testid="idea-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{IDEA_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={dialog.priority} onValueChange={(v) => setDialog({ ...dialog, priority: v })}>
                    <SelectTrigger data-testid="idea-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{IDEA_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Sub-ideas <span className="text-xs text-muted-foreground">(smaller items that fall under this)</span></Label>
                <div className="space-y-1.5">
                  {(dialog.subideas || []).map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-2" data-testid={`idea-dialog-sub-${i}`}>
                      <Checkbox checked={!!s.done} onCheckedChange={(c) => setDialog((d) => ({ ...d, subideas: d.subideas.map((x, j) => (j === i ? { ...x, done: !!c } : x)) }))} aria-label="Toggle sub-idea" />
                      <Input value={s.text} onChange={(e) => setDialog((d) => ({ ...d, subideas: d.subideas.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }))} className="h-8 flex-1 text-sm" data-testid={`idea-dialog-sub-input-${i}`} />
                      <button onClick={() => setDialog((d) => ({ ...d, subideas: d.subideas.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-destructive" data-testid={`idea-dialog-sub-del-${i}`}><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => setDialog((d) => ({ ...d, subideas: [...(d.subideas || []), { id: genSubId(), text: "", done: false }] }))} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="idea-dialog-sub-add"><Plus className="h-3.5 w-3.5" /> Add sub-idea</button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="idea-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{dialog?.id ? "Save Changes" : "Add Idea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
